/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FoodOption,
  officePreference,
} from "components/rep/accounts/cardNewDesign/types";
import React, { Dispatch, FC, createContext, useReducer } from "react";

type Action = {
  type:
    | "SET_APPOINTMENT_REQUIREMENTS"
    | "SET_APPOINTMENT_DETAILS"
    | "SET_REPS"
    | "UPDATE_REP"
    | "FETCH_REQUEST"
    | "FETCH_COMPLETE"
    | "INITIAL_DATA_FETCHED"
    | "SET_RESTURANT_PREFERENCES"
    | "SET_AVAILABLE_PRACTICE_TYPES"
    | "SET_OFFICE_PRACTICE_TYPES"
    | "SET_DELEGATED_USERS"
    | "SET_APPOINTMENT_TEMPLATES"
    | "SET_OFF_DAYS_TEMPLATES"
    | "SET_DIETARY"
    | "SET_KIOSK_USERS"
    | "SET_KIOSK_APPOINTMENTS"
    | "SET_KIOSK_REPS"
    | "SET_KIOSK_APPOINTMENT_GROUPING"
    | "SET_KIOSK_REPS_SEARCH_TERM"
    | "RESET_KIOSK_UI"
    | "SHOW_APPOINTMENT_UPDATE_MODAL"
    | "HIDE_APPOINTMENT_UPDATE_MODAL"
    | "SHOW_TEMPLATE_REMOVAL_SUCCESS_MODAL"
    | "HIDE_TEMPLATE_REMOVAL_SUCCESS_MODAL"
    | "SHOW_TEMPLATE_REMOVAL_ERROR_MODAL"
    | "HIDE_TEMPLATE_REMOVAL_ERROR_MODAL";
  payload: JObj;
};
export interface iAppointmentTemplate {
  dateTimeSlots?: string[];
  days?: number[];
  id?: number;
  type?: string;
  meetingFormat?: string;
  apptRequirements?: string;
  appointmentIds?: number[];
  startDate?: string;
  endDate?: string;
  annotationColor?: string | null;
  annotationTitle?: string | null;
  annotationIcon?: string | null;
}
export interface iOffDaysTemplate {
  dateTimeSlots?: string[];
  days?: number[];
  id?: number;
  title?: string;
  offDayIds?: number[];
  notes?: string;
  startDate?: string;
  endDate?: string;
}
export interface iAppointmentDetails {
  id?: number;
  clientId?: number;
  type?: string;
  date?: string;
  time?: number;
  confirmation?: number;
  booked?: number;
  bookedByRep?: number;
  virtualOptions?: string;
  apptRequirements?: string;
  visibilityOverride?: string | null;
  reminderNotificationSentToRepAt?: string | null;
  isRecurrenceSeries?: number;
  instanceOfRecurrenceSeries?: number | null;
  bookedAt?: string | null;
  reminderNotificationSentToOfficeAt?: string | null;
  endDate?: string | null;
  recurrenceFrequency?: string | null;
  foodOptions?: FoodOption[];
  preferences?: officePreference[];
}

interface Data {
  appointmentRequirements: JObj;
  reps: Array<JObj>;
  isLoading: boolean;
  initialDataFetched: boolean;
  virtualOptions: Array<string>;
  dietary: JObj;
  resturantPreferences: Array<JObj>;
  officePracticeTypes: Array<JObj>;
  availablePracticeTypes: Array<JObj>;
  delegatedUsers: Array<JObj>;
  appointmentDetails: iAppointmentDetails[] | [];
  appointmentTemplates: iAppointmentTemplate[];
  offDaysTemplates: iOffDaysTemplate[];
  kioskUsers: Array<JObj>;
  kioskUsersLastFetchedAt: number | null;
  kioskAppointments: Array<JObj>;
  kioskAppointmentsLastFetchedAt: number | null;
  kioskReps: Array<JObj>;
  kioskRepsLastFetchedAt: number | null;
  kioskAppointmentGrouping?: string;
  kioskRepsSearchTerm?: string;
  showAppointmentUpdateModal?: boolean;
  showTemplateRemovalSuccessModal?: boolean;
  showTemplateRemovalErrorModal?: boolean;
  templateRemovalModalMessage?: string;
  appointmentUpdateModalData?: {
    appointmentInfo: {
      date: string;
      time: string;
      type: string;
    };
    repInfo?: {
      name: string;
      company: string;
      phone: string;
      email: string;
      photoUrl: string;
      status: "approved" | "pending" | "denied";
    } | null;
    foodInfo?: {
      oldSelections: Array<{
        id: number;
        name: string;
        image: string | null;
        rating: number | null;
        price: string | null;
        address: string | null;
      }>;
      newSelections: Array<{
        id: string;
        name: string;
        imageUrl: string;
        rating: number;
        price?: string;
        address: string;
      }>;
    } | null;
    invitedRepEmail?: string;
    changes: Array<{
      field: string;
      label: string;
      oldValue: string | null;
      newValue: string;
    }>;
  } | null;
}

interface ContextType {
  data: Data;
  dispatchAction: Dispatch<Action>;
}

const initialData: Data = {
  appointmentRequirements: {},
  reps: [],
  isLoading: false,
  initialDataFetched: false,
  virtualOptions: ["Virtual only", "Virtual optional", "In person only"],
  dietary: {},
  resturantPreferences: [],
  officePracticeTypes: [],
  availablePracticeTypes: [],
  delegatedUsers: [],
  appointmentDetails: [],
  appointmentTemplates: [],
  offDaysTemplates: [],
  kioskUsers: [],
  kioskUsersLastFetchedAt: null,
  kioskAppointments: [],
  kioskAppointmentsLastFetchedAt: null,
  kioskReps: [],
  kioskRepsLastFetchedAt: null,
  kioskAppointmentGrouping: "week",
  kioskRepsSearchTerm: "",
  showAppointmentUpdateModal: false,
  appointmentUpdateModalData: null,
  showTemplateRemovalSuccessModal: false,
  showTemplateRemovalErrorModal: false,
  templateRemovalModalMessage: "",
};

export const ClientDataContext = createContext<ContextType | undefined>(
  undefined,
);

const reducer = (data: Data, action: Action) => {
  switch (action.type) {
    case "SET_APPOINTMENT_REQUIREMENTS":
      return {
        ...data,
        appointmentRequirements: action.payload.appointmentRequirements,
      };
    case "SET_APPOINTMENT_DETAILS":
      return { ...data, appointmentDetails: action.payload.appointmentDetails };

    case "SET_REPS":
      return { ...data, reps: action.payload.reps };
    case "SET_APPOINTMENT_TEMPLATES":
      return {
        ...data,
        appointmentTemplates: action.payload.appointmentTemplates,
      };
    case "SET_OFF_DAYS_TEMPLATES":
      return {
        ...data,
        offDaysTemplates: action.payload.offDaysTemplates,
      };
    case "UPDATE_REP": {
      const { id: idToUpdate, changes } = action.payload;

      if (!idToUpdate) return data;

      const newReps = [...data.reps];
      const repIdxToUpdate = newReps.findIndex((a) => a.id === idToUpdate);

      if (repIdxToUpdate < 0) return data;

      newReps[repIdxToUpdate] = { ...newReps[repIdxToUpdate], ...changes };

      return { ...data, reps: newReps };
    }

    case "SET_RESTURANT_PREFERENCES":
      return {
        ...data,
        resturantPreferences: action.payload.resturantPreferences,
      };
    case "SET_AVAILABLE_PRACTICE_TYPES":
      return {
        ...data,
        availablePracticeTypes: action.payload.availablePracticeTypes,
      };
    case "SET_OFFICE_PRACTICE_TYPES":
      return {
        ...data,
        officePracticeTypes: action.payload.officePracticeTypes,
      };
    case "SET_DIETARY":
      return { ...data, dietary: action.payload.dietary };
    case "SET_DELEGATED_USERS":
      return { ...data, delegatedUsers: action.payload.delegatedUsers };
    case "FETCH_REQUEST":
      return { ...data, isLoading: true };
    case "FETCH_COMPLETE":
      return { ...data, isLoading: false };
    case "INITIAL_DATA_FETCHED":
      return { ...data, initialDataFetched: true };
    case "SET_KIOSK_USERS":
      return {
        ...data,
        kioskUsers: action.payload.kioskUsers || [],
        kioskUsersLastFetchedAt: action.payload.kioskUsersLastFetchedAt || null,
      };
    case "SET_KIOSK_APPOINTMENTS":
      return {
        ...data,
        kioskAppointments: action.payload.kioskAppointments || [],
        kioskAppointmentsLastFetchedAt:
          action.payload.kioskAppointmentsLastFetchedAt || null,
      };
    case "SET_KIOSK_REPS":
      return {
        ...data,
        kioskReps: action.payload.kioskReps || [],
        kioskRepsLastFetchedAt: action.payload.kioskRepsLastFetchedAt || null,
      };
    case "SET_KIOSK_APPOINTMENT_GROUPING":
      return {
        ...data,
        kioskAppointmentGrouping:
          action.payload.kioskAppointmentGrouping || "week",
      };
    case "SET_KIOSK_REPS_SEARCH_TERM":
      return {
        ...data,
        kioskRepsSearchTerm: action.payload.kioskRepsSearchTerm || "",
      };
    case "RESET_KIOSK_UI":
      return {
        ...data,
        kioskAppointmentGrouping: "week",
        kioskRepsSearchTerm: "",
      };
    case "SHOW_APPOINTMENT_UPDATE_MODAL":
      return {
        ...data,
        showAppointmentUpdateModal: true,
        appointmentUpdateModalData: action.payload.appointmentUpdateModalData,
      };
    case "HIDE_APPOINTMENT_UPDATE_MODAL":
      return {
        ...data,
        showAppointmentUpdateModal: false,
        appointmentUpdateModalData: null,
      };
    case "SHOW_TEMPLATE_REMOVAL_SUCCESS_MODAL":
      return {
        ...data,
        showTemplateRemovalSuccessModal: true,
        templateRemovalModalMessage:
          action.payload.templateRemovalModalMessage || "",
      };
    case "HIDE_TEMPLATE_REMOVAL_SUCCESS_MODAL":
      return {
        ...data,
        showTemplateRemovalSuccessModal: false,
        templateRemovalModalMessage: "",
      };
    case "SHOW_TEMPLATE_REMOVAL_ERROR_MODAL":
      return {
        ...data,
        showTemplateRemovalErrorModal: true,
        templateRemovalModalMessage:
          action.payload.templateRemovalModalMessage || "",
      };
    case "HIDE_TEMPLATE_REMOVAL_ERROR_MODAL":
      return {
        ...data,
        showTemplateRemovalErrorModal: false,
        templateRemovalModalMessage: "",
      };
    default:
      return data;
  }
};

export const ClientDataContextProvider: FC<{ children: any }> = ({
  children,
}) => {
  const [data, dispatchAction] = useReducer(reducer, initialData);
  return (
    <ClientDataContext.Provider value={{ data, dispatchAction }}>
      {children}
    </ClientDataContext.Provider>
  );
};
